import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Footprints, Percent, Receipt, CalendarDays, ArrowRight, Sparkles, Building2, Wallet, TrendingUp, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiTile, KpiSkeleton, DeltaBadge, fadeUp } from "@/components/exec/KpiTile";
import { SectionCard, PageHeader, ErrorState, ChartSkeleton, Stat } from "@/components/exec/Primitives";
import { TrendChart, HolidayLegend } from "@/components/exec/Charts";
import { StoreTable, PatternSignals, ScopeLink, DataNote } from "@/components/exec/Widgets";
import { FeedAlertBanner } from "@/components/dashboard/FeedAlertBanner";
import { ForecastBriefCard } from "@/components/exec/ForecastBrief";
import { useSummaryV2, useForecastBrief } from "@/lib/queries";
import { useScope } from "@/lib/scope";
import { useApp } from "@/lib/app";
import { errorMessage } from "@/lib/api";
import { fmtInt, fmtPct, fmtMYRShort, fmtDate, fmtDateLong, fmtTimeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

const EVENT_DOT = {
  public_holiday: { MY: "bg-holiday-my", SG: "bg-holiday-sg", JOHOR: "bg-holiday-johor" },
  school_holiday: "bg-holiday-school",
  payday: "bg-holiday-payday",
  demand: "bg-holiday-demand",
  custom: "bg-holiday-custom",
};
export const eventDot = (e) => (e.type === "public_holiday" ? EVENT_DOT.public_holiday[e.market] || EVENT_DOT.public_holiday.MY : EVENT_DOT[e.type] || EVENT_DOT.custom);
const daysAway = (n) => (n === 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`);
const na = (k, txt) => (k && (k.value === null || k.value === undefined) ? txt : undefined);

export default function ExecutiveSummary() {
  const navigate = useNavigate();
  const { setScope } = useScope();
  const { user, isAdmin, feed } = useApp();
  const q = useSummaryV2();
  const d = q.data;
  const fq = useForecastBrief("all", 30);

  useEffect(() => {
    document.title = "Zapin | Executive Summary";
  }, []);

  const openScope = (id) => {
    setScope(id);
    navigate("/count");
  };

  const count = d?.insights?.count;
  const growth = d?.insights?.growth;
  const firstName = (user?.name || user?.login || "").split(" ")[0];

  return (
    <div className="space-y-6 md:space-y-8" data-testid="executive-summary-page">
      <PageHeader
        eyebrow={`Executive summary · all stores${d?.anchor ? ` · data to ${fmtDateLong(d.anchor)}` : ""}`}
        title={firstName ? `Good day, ${firstName}. Here is the group.` : "The group at a glance"}
        subtitle={d ? `${d.period.label} · ${d.period.compare_label}. Eight duty-free doors across Forest City, Stulang and Senai.` : "Loading the latest period…"}
        testId="executive-summary-header"
        actions={
          <>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => navigate("/count")} data-testid="summary-open-count">
              <Users size={14} /> Count analytics
            </Button>
            <Button size="sm" className="h-9 gap-1.5" onClick={() => navigate("/ai-growth")} data-testid="summary-open-growth">
              <Sparkles size={14} /> AI growth
            </Button>
          </>
        }
      />

      <FeedAlertBanner feed={feed} siteIds={(d?.stores || []).map((s) => s.id)} canOpenFeed={isAdmin} onNavigate={() => navigate("/admin/live")} className="print-hidden" />

      {q.isError && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} testId="summary-error" />}

      {/* KPI strip */}
      {!d ? (
        <KpiSkeleton n={8} />
      ) : (
        <>
          <DataNote coverage={d.coverage} period={d.period} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="summary-kpi-strip">
            <KpiTile index={0} label="Visitors" kpi={d.kpis.visitors} icon={Users} compareLabel={d.period.compare_label} hint="People who entered any of the eight stores on closed trading days in this period." testId="kpi-visitors" />
            <KpiTile index={1} label="Avg visitors / day" kpi={d.kpis.avg_daily} icon={TrendingUp} testId="kpi-avg-daily" />
            <KpiTile index={2} label="Walk-in rate" kpi={d.kpis.walk_in_rate} icon={Footprints} sub={na(d.kpis.walk_in_rate, "passing-by not captured")} hint="Visitors as a share of everyone who walked past the door (visitors + passers-by)." testId="kpi-walk-in" />
            <KpiTile index={3} label="Female share" kpi={d.kpis.female_share} icon={Percent} sub={na(d.kpis.female_share, "gender not captured")} testId="kpi-female-share" />
            <KpiTile index={4} label="Revenue" kpi={d.kpis.revenue} icon={Wallet} sub={na(d.kpis.revenue, d.sales_available ? "no sales in period" : "no POS data")} hint="Sum of receipts from the stores with a POS ledger, on days with footfall counts." testId="kpi-revenue" />
            <KpiTile index={5} label="Conversion" kpi={d.kpis.conversion} icon={Receipt} sub={na(d.kpis.conversion, "needs POS")} hint="Receipts as a share of visitors (POS stores only)." testId="kpi-conversion" />
            <KpiTile index={6} label="Avg basket" kpi={d.kpis.atv} icon={Receipt} sub={na(d.kpis.atv, "needs POS")} testId="kpi-atv" />
            <KpiTile index={7} label="Families share" kpi={d.kpis.families_share} icon={Users} sub={na(d.kpis.families_share, "age not captured")} hint="Children, teenagers and adolescents as a share of visitors with an age estimate." testId="kpi-families" />
          </div>
        </>
      )}

      {/* forecast brief */}
      <ForecastBriefCard query={fq} />

      {/* trend + AI + upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <SectionCard className="lg:col-span-8" title="Group footfall, day by day" subtitle={d ? `${d.period.label} against the previous window. Holiday, school-holiday and payday markers follow the overlay toggle.` : undefined} testId="summary-trend-card" actions={<ScopeLink to="/count">Full count analytics</ScopeLink>}>
          {!d ? <ChartSkeleton height={320} /> : <TrendChart rows={d.daily} prevRows={d.prev_daily} events={d.calendar.events} height={320} testId="summary-trend-chart" />}
          <HolidayLegend className="mt-3" />
          {d && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              <Stat label="Peak day" value={d.kpis.peak?.value ? fmtInt(d.kpis.peak.value) : "—"} sub={d.kpis.peak?.date ? fmtDateLong(d.kpis.peak.date) : ""} testId="summary-peak" />
              <Stat label="Quietest day" value={d.kpis.low?.value ? fmtInt(d.kpis.low.value) : "—"} sub={d.kpis.low?.date ? fmtDateLong(d.kpis.low.date) : ""} testId="summary-low" />
              <Stat label="Weekend share" value={fmtPct(d.kpis.weekend_share?.value, 0)} sub="of visitors" testId="summary-weekend-share" />
              <Stat label="Stores reporting" value={`${d.coverage.stores_with_data} / ${d.coverage.stores_total}`} sub={`${d.coverage.active_days} closed day${d.coverage.active_days === 1 ? "" : "s"} counted`} testId="summary-stores-reporting" />
            </div>
          )}
        </SectionCard>

        <div className="lg:col-span-4 flex flex-col gap-4">
          <SectionCard title="AI executive read" subtitle="Nightly at 06:00 MYT · on demand any time" testId="summary-ai-card" actions={<Sparkles size={15} className="text-gold" />}>
            {!d ? (
              <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-4 w-2/3" /></div>
            ) : count?.status === "ready" && count.data ? (
              <div className="space-y-3">
                <p className="text-sm leading-relaxed" data-testid="summary-ai-text">{count.data.executive_summary}</p>
                {count.data.insights?.slice(0, 2).map((i) => (
                  <div key={i.id} className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2 text-xs flex items-start gap-2" data-testid="summary-ai-insight">
                    <Badge variant="outline" className={cn("text-[10px] font-mono uppercase shrink-0", i.impact === "high" ? "border-gold/30 bg-gold/15 text-gold" : "border-border/70 bg-muted/20 text-muted-foreground")}>{i.impact}</Badge>
                    <span className="leading-snug">{i.headline}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="font-mono">{fmtTimeAgo(count.generated_at)}</span>
                  <ScopeLink to="/count#insights">All insights</ScopeLink>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 p-4 text-sm text-muted-foreground" data-testid="summary-ai-empty">
                {count?.status === "generating" ? "The consultant is reading this week's evidence pack…" : "No AI read for this period yet."}
                <div className="mt-2"><ScopeLink to="/count#insights">Generate on Count Analytics</ScopeLink></div>
              </div>
            )}
            {growth?.status === "ready" && growth.data?.recommendations?.[0] && (
              <div className="mt-3 pt-3 border-t border-border/50 text-xs" data-testid="summary-growth-teaser">
                <div className="eyebrow mb-1">Top growth move</div>
                <div className="font-medium leading-snug">{growth.data.recommendations[0].title}</div>
                <div className="text-muted-foreground mt-0.5">{growth.data.recommendations[0].expected_impact}</div>
                <Link to="/ai-growth" className="text-gold hover:underline inline-flex items-center gap-1 mt-1.5 focus-gold rounded" data-testid="summary-open-growth-link">Open AI Growth <ArrowRight size={11} /></Link>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Next 30 days" subtitle="Holidays, paydays and your events" testId="summary-upcoming-card" actions={<ScopeLink to="/calendar">Calendar</ScopeLink>}>
            {!d ? (
              <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
            ) : d.calendar.upcoming?.length ? (
              <ul className="space-y-2" data-testid="summary-upcoming-list">
                {d.calendar.upcoming.map((e) => (
                  <li key={e.id} className="flex items-start gap-2.5 text-sm" data-testid="summary-upcoming-item">
                    <span className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", eventDot(e))} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{e.name}</span>
                        <span className="font-mono text-[11px] text-muted-foreground shrink-0">{daysAway(e.days_away)}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">{e.start === e.end ? fmtDateLong(e.start) : `${fmtDate(e.start)} – ${fmtDate(e.end)}`}{e.markets?.length ? ` · ${e.markets.join(" + ")}` : ""}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground" data-testid="summary-upcoming-empty">Nothing on the calendar in the next 30 days.</div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* malls */}
      <section data-testid="summary-clusters">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="eyebrow">By mall</div>
            <h2 className="font-display text-lg md:text-xl">Three locations, one catchment story</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {!d
            ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)
            : d.clusters.map((c, i) => (
                <motion.button
                  key={c.id}
                  type="button"
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                  custom={i}
                  onClick={() => openScope(`cluster:${c.id}`)}
                  data-testid={`cluster-card-${c.id}`}
                  className="card-surface card-hover p-4 md:p-5 text-left focus-gold flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-base md:text-lg leading-tight">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{c.location}</div>
                    </div>
                    <span className="h-8 w-8 rounded-lg bg-gold/12 text-gold flex items-center justify-center shrink-0"><Building2 size={15} /></span>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="font-display text-2xl tabular leading-none" data-testid={`cluster-visitors-${c.id}`}>{c.visitors ? fmtInt(c.visitors) : "—"}</div>
                    {c.delta !== null && c.delta !== undefined ? <DeltaBadge delta={c.delta} tone={c.delta > 0 ? "positive" : c.delta < 0 ? "negative" : "neutral"} testId={`cluster-delta-${c.id}`} /> : <span className="text-[11px] text-muted-foreground">{c.stores_with_data ? "no comparison" : "awaiting data"}</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground border-t border-border/50 pt-3">
                    <div><div className="uppercase tracking-wider text-[10px]">Stores</div><div className="font-mono text-foreground mt-0.5">{c.stores_with_data}/{c.stores}</div></div>
                    <div><div className="uppercase tracking-wider text-[10px]">Walk-in</div><div className="font-mono text-foreground mt-0.5">{c.walk_in_rate !== null && c.walk_in_rate !== undefined ? fmtPct(c.walk_in_rate) : "n/c"}</div></div>
                    <div><div className="uppercase tracking-wider text-[10px]">Revenue</div><div className="font-mono text-foreground mt-0.5">{c.stores_with_pos && c.revenue ? fmtMYRShort(c.revenue) : c.stores_with_pos ? "RM 0" : "no POS"}</div></div>
                  </div>
                  {c.leader && <div className="text-[11px] text-muted-foreground">Busiest door: <span className="text-foreground">{c.leader}</span></div>}
                </motion.button>
              ))}
        </div>
      </section>

      {/* leaderboard + signals */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <SectionCard className="lg:col-span-7" title="Store leaderboard" subtitle="Ranked by visitors in the period. Click a store to open it in Count Analytics." testId="summary-leaderboard-card" actions={<Layers size={15} className="text-gold" />}>
          {!d ? <Skeleton className="h-64 w-full" /> : <StoreTable stores={d.stores} compareLabel="vs previous" onPick={openScope} testId="summary-store-table" />}
        </SectionCard>
        <SectionCard className="lg:col-span-5" title="What the numbers say" subtitle="Deterministic pattern signals, strongest first - no AI involved." testId="summary-patterns-card">
          {!d ? <Skeleton className="h-64 w-full" /> : <PatternSignals patterns={d.patterns} limit={5} testId="summary-pattern-signals" />}
          {d && !d.sales_available && (
            <div className="mt-3 rounded-lg border border-dashed border-border/70 bg-muted/10 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="summary-no-pos-note">
              <Receipt size={13} className="mt-0.5 shrink-0 text-gold" />
              <span>No POS receipts fall inside this window, so revenue, conversion and basket are blank. {isAdmin ? <Link to="/sales" className="text-gold hover:underline">Upload a POS report</Link> : "An admin can upload a POS report"} or widen the period.</span>
            </div>
          )}
        </SectionCard>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground print-hidden">
        <CalendarDays size={12} /> Standard week and month views are refreshed every night at 06:00 MYT. Period and overlay choices are remembered on this device.
      </div>
    </div>
  );
}
